import { ChangeEvent, useState, MouseEvent } from "react";
import { useDebouncedEffect } from "../../App/State/Hooks/useDebouncedEffect";
import { Box, TextField } from "@mui/material";
import { ITableColumn } from "../../App/Typings/TableColumn";
import SortableTable from "../../App/Components/Table/SortableTable";
import { ITablePagination } from "../../App/Typings/SortableTable";
import { IMessagePerformanceDto } from "../../App/Typings/MessageText";
import createSlug from "../../App/Utilities/createSlug";
import {
  PerformanceProgressBar,
  IPerformanceThreshold,
} from "../../GroupProfile/Components/PerformanceProgressBar";
import { MessageDeliveryType } from "../../App/Enums/Message";
import { useGetMessagePerformanceIndividual } from "../../App/Queries/ReportingQueries";
import Modal from "../../App/Components/Modal/Modal";
import IndividualEmailCollectionDisplay from "./IndividualEmailDisplay";

export const IndividualEmailPerformance = ({
  groupId,
  startDate,
  endDate,
  queryEnabled = true,
  defaultPageSize = 10,
  hideEditColumns = false,
}: {
  groupId?: string;
  startDate?: Date;
  endDate?: Date;
  queryEnabled?: boolean;
  defaultPageSize?: number;
  hideEditColumns?: boolean;
}) => {
  const [inputValue, setInputValue] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [orderBy, setOrderBy] = useState<string | undefined>("sentDate");
  const [searchQuery, setSearchQuery] = useState<string | undefined>();
  const [selectedCollection, setSelectedCollection] = useState<IMessagePerformanceDto | undefined>();
  const [displayCollectionDialog, setDisplayCollectionDialog] = useState(false);
  const { data, isLoading } = useGetMessagePerformanceIndividual(
    MessageDeliveryType.Email,
    page + 1,
    pageSize,
    searchQuery,
    orderBy,
    order,
    groupId,
    startDate,
    endDate,
    queryEnabled,
  );

  const items = data?.items ?? [];
  const itemCount = items.length;
  const totalCount = data?.totalItems ?? 0;
  const totalCountDisplay = totalCount > pageSize ? totalCount : itemCount;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangePageSize = (event: ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (event: MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy?.toLowerCase() === property.toLowerCase() && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelected = (selected: IMessagePerformanceDto) => {
    setSelectedCollection(selected);
    setDisplayCollectionDialog(true);
  };

  const handleCloseCollectionDialog = () => {
    setDisplayCollectionDialog(false);
    setTimeout(() => {
      setSelectedCollection(undefined);
    }, 300);
  };

  useDebouncedEffect(
    () => {
      setSearchQuery(inputValue);
      setPage(0);
    },
    [inputValue],
    300,
  );

  return (
    <Box>
      <TextField
        data-test="group-email-search"
        label="Search"
        placeholder="Search by email name or date sent"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        type="search"
        sx={{
          width: "50ch",
          maxWidth: "100%",
          marginBottom: 1,
        }}
      />
      <SortableTable
        name="group-individual-emails"
        columns={columnNames}
        loading={isLoading}
        tableData={items}
        pagination={{
          ...paginationOptions,
          currentPage: page,
          rows: pageSize,
          handleChangePage: handleChangePage,
          handleChangeRowsPerPage: handleChangePageSize,
          totalCount: totalCountDisplay,
        }}
        handleSelected={handleSelected}
        emptyMessage="No Email Data Available"
        exportFileName="group-email-performance"
        exportEnabled={true}
        renderCustomCell={getPerformanceCell}
        customOrder={order}
        customOrderBy={orderBy}
        customRequestSort={handleRequestSort}
        useServerSidePagination={true}
        rowTitle={(row) => row["name"] as string}
        getRowId={(row) => createSlug(row["name"] as string)}
        columnEditEnabled={!hideEditColumns}
        disableTruncation={true}
      />
      <Modal
        title={`Message Content: ${selectedCollection?.name ?? ""}`}
        size="lg"
        dataTestId="collection-dialog-email"
        isOpen={displayCollectionDialog}
        handleCloseModal={handleCloseCollectionDialog}
        content={
          selectedCollection ? (
            <IndividualEmailCollectionDisplay selectedCollection={selectedCollection} />
          ) : (
            <></>
          )
        }
        footerContent={<></>}
      />
    </Box>
  );
};

const paginationOptions: ITablePagination = {
  rowsPerPage: [10, 15, 25],
  enabled: true,
};

const columnNames: ITableColumn[] = [
  {
    label: "Email Name",
    propName: "name",
    order: 0,
  },
  {
    label: "Date Sent",
    propName: "sentDate",
    order: 1,
  },
  {
    label: "Emails Delivered",
    propName: "deliveredCount",
    order: 2,
  },
  {
    label: "Unique Emails Opened",
    propName: "openedCount",
    order: 3,
  },
  {
    label: "Emails Clicked",
    propName: "clickedCount",
    order: 4,
  },
  {
    label: "Unsubscribes",
    propName: "unsubscribedCount",
    order: 5,
  },
];

const getPerformanceCell = (row: IMessagePerformanceDto, column: ITableColumn): React.ReactNode => {
  const value = row[column.propName];
  if (column.propName === "sentDate") {
    return value ? new Date(value).toLocaleDateString() : "";
  }
  if (column.propName === "deliveredCount") {
    return value ?? 0;
  }
  if (
    column.propName === "openedCount" ||
    column.propName === "clickedCount" ||
    column.propName === "unsubscribedCount"
  ) {
    const count = value ?? 0;
    let rate = 0;
    if (column.propName === "openedCount") {
      const deliveredCount = row["deliveredCount"] ?? 0;
      rate = deliveredCount > 0 ? (count / deliveredCount) * 100 : 0;
    } else if (column.propName === "clickedCount" || column.propName === "unsubscribedCount") {
      const openedCount = row["openedCount"] ?? 0;
      rate = openedCount > 0 ? (count / openedCount) * 100 : 0;
    }
    rate = isNaN(rate) ? 0 : parseFloat(rate.toFixed(3));

    return (
      <PerformanceProgressBar
        count={count}
        rate={rate}
        reverse={column.propName === "unsubscribedCount"}
        threshold={eventTypePercentThresholds[column.propName]}
      />
    );
  }
  return value;
};

const eventTypePercentThresholds = {
  openedCount: {
    low: 33.333,
    high: 66.667,
  },
  clickedCount: {
    low: 33.333,
    high: 66.667,
  },
  unsubscribedCount: {
    low: 10,
    high: 25,
  },
} as Record<string, IPerformanceThreshold>;
